import Layout from "../components/Layout";
import { Link } from "react-router-dom";
import { 
  ExternalLink, 
  FileText, 
  Calendar, 
  Users, 
  BookOpen, 
  FolderOpen, 
  MessageSquare,
  AlertCircle,
  Bot,
  Calculator,
  Shield,
  ClipboardCheck,
  FileSearch,
  TrendingUp
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";

const externalLinks = [
  {
    title: "CompliMind (CMS)",
    description: "Single source of truth for policies, contracts, and compliance documents",
    url: "https://CMS.CompliSun.com",
    icon: Shield,
  },
  {
    title: "Board Disclosures",
    description: "Investment Adviser Disclosure Forms",
    url: "https://www.complisun.com/pointe-capital-investment-adviser-disclosure-form/",
    icon: FileText,
  },
  {
    title: "Compliance Calendar",
    description: "Important dates and deadlines",
    url: "https://www.complisun.com/pointe-capital-calendar/",
    icon: Calendar,
  },
  {
    title: "Trade Pre-Clearance",
    description: "Employee Trade Pre-Clearance Request",
    url: "https://www.complisun.com/trade-clearance/",
    icon: ClipboardCheck,
  },
  {
    title: "Employee Directory",
    description: "Pointe Capital team contacts",
    url: "https://www.complisun.com/employee-directory-pointe-capital/",
    icon: Users,
  },
  {
    title: "Training Courses",
    description: "Available compliance courses",
    url: "https://www.complisun.com/pointe-capital-courses/",
    icon: BookOpen,
  },
  {
    title: "Resources & Policies",
    description: "Core policies and resource library",
    url: "https://www.complisun.com/resource-pointe-capital/",
    icon: FolderOpen,
  },
  {
    title: "Private Chatroom",
    description: "Team communication channel",
    url: "https://www.complisun.com/chat-for-pointe-capital/",
    icon: MessageSquare,
  },
  {
    title: "Issue Management",
    description: "Track and manage compliance issues",
    url: "https://www.complisun.com/issue-management/",
    icon: AlertCircle,
  },
];

const aiAgents = [
  {
    title: "Marketing Review Agent",
    description: "Submit marketing materials for AI compliance analysis",
    path: "/agents/marketing",
    icon: FileSearch,
    color: "bg-blue-50 text-blue-700 border-blue-200",
  },
  {
    title: "Research Agent",
    description: "AI-powered company research and due diligence",
    path: "/agents/research",
    icon: TrendingUp,
    color: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  {
    title: "SEC Compliance Monitor",
    description: "Check content for SEC compliance violations",
    path: "/agents/sec-compliance",
    icon: Shield,
    color: "bg-purple-50 text-purple-700 border-purple-200",
  },
  {
    title: "Form ADV / Regulatory Agent",
    description: "Track SEC updates and Form ADV requirements",
    path: "/agents/form-adv",
    icon: FileText,
    color: "bg-amber-50 text-amber-700 border-amber-200",
  },
];

export default function Dashboard() {
  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard">
        {/* Header */}
        <div className="animate-fade-in-up">
          <p className="label-text mb-2">Dashboard</p>
          <h1 className="heading-xl">PCM CompliSun Guardian</h1>
          <p className="body-text mt-2 max-w-2xl">
            Your AI-powered compliance command center. Access tools, resources, and intelligent agents 
            to streamline your compliance workflow.
          </p>
        </div>

        {/* AI Agents Section */}
        <section className="animate-fade-in-up animation-delay-100">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="heading-md flex items-center gap-2">
                <Bot className="h-6 w-6 text-[#8B0000]" />
                AI Compliance Agents
              </h2>
              <p className="text-slate-500 text-sm mt-1">Intelligent tools for compliance analysis</p>
            </div>
            <Link to="/agents" className="text-sm text-[#8B0000] hover:underline font-medium">
              View All Agents →
            </Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {aiAgents.map((agent) => {
              const Icon = agent.icon;
              return (
                <Link
                  key={agent.path}
                  to={agent.path}
                  className="control-card group hover:border-[#8B0000]"
                  data-testid={`agent-card-${agent.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className={`w-10 h-10 rounded flex items-center justify-center mb-3 ${agent.color}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-semibold text-slate-900 group-hover:text-[#8B0000] transition-colors">
                    {agent.title}
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">{agent.description}</p>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Risk Assessment Tool */}
        <section className="animate-fade-in-up animation-delay-200">
          <Link
            to="/risk-assessment"
            className="block control-card hover:border-[#8B0000] bg-gradient-to-r from-[#8B0000]/5 to-transparent"
            data-testid="risk-assessment-card"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded bg-[#8B0000] flex items-center justify-center">
                <Calculator className="h-7 w-7 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-slate-900">Risk Assessment Tool</h3>
                <p className="text-slate-500">Evaluate investment risk profiles and get personalized allocation recommendations</p>
              </div>
            </div>
          </Link>
        </section>

        {/* Quick Links Section */}
        <section className="animate-fade-in-up animation-delay-300">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="heading-md">Quick Links</h2>
              <p className="text-slate-500 text-sm mt-1">Access essential compliance resources</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {externalLinks.map((link) => {
              const Icon = link.icon;
              return (
                <a
                  key={link.url}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="control-card group flex items-start gap-4"
                  data-testid={`link-${link.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center flex-shrink-0 group-hover:bg-[#8B0000] transition-colors">
                    <Icon className="h-5 w-5 text-slate-600 group-hover:text-white transition-colors" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900 group-hover:text-[#8B0000] transition-colors truncate">
                        {link.title}
                      </h3>
                      <ExternalLink className="h-3 w-3 text-slate-400 flex-shrink-0" />
                    </div>
                    <p className="text-sm text-slate-500 mt-0.5">{link.description}</p>
                  </div>
                </a>
              );
            })}
          </div>
        </section>

        {/* Contact CCO Section */}
        <section className="animate-fade-in-up animation-delay-400">
          <Card className="border-slate-200 bg-slate-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Need Compliance Guidance?</CardTitle>
              <CardDescription>
                Contact Chief Compliance Officer Colin Darke for personalized support
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                <a 
                  href="mailto:colindarke@complisun.com" 
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="contact-cco-btn"
                >
                  Contact Colin Darke
                </a>
                <a 
                  href="mailto:pointecapital@complisun.com" 
                  className="btn-secondary inline-flex items-center gap-2"
                  data-testid="contact-team-btn"
                >
                  Email Compliance Team
                </a>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </Layout>
  );
}
